/**
 * Cart readiness - progressive-disclosure shipping estimator (ledger v2).
 *
 * States per [data-cr-ship] block:
 *   A collapsed  - "Estimate shipping" link (server-rendered default)
 *   B expanded   - postal input + Estimate button
 *   C estimated  - ledger row "Shipping estimate . POSTAL  $X" + carrier sub-line
 * A saved postal (localStorage) auto-upgrades A -> C on load.
 *
 * Ledger total anchor: when an estimate lands, the nearest [data-crl] panel's
 * data-cr-total-* elements flip from "Subtotal, before shipping and tax" to
 * "Estimated total, plus tax" = cart total (data-cr-total-cents) + cheapest
 * rate. Reverted whenever the estimate is cleared. Pages without a [data-crl]
 * panel (/cart summary) simply skip the anchor update.
 *
 * Rates: GET /cart/shipping_rates.json - same endpoint/waterfall as checkout
 * (ClickShip live rates). Shopify computes carrier rates asynchronously, so
 * the first request for a new address may be empty: poll up to 4 times.
 *
 * Document-level delegated listeners + a re-boot MutationObserver, so it
 * survives drawer/section re-renders without rebinding (cart-enhancements.js
 * pattern).
 */
(() => {
  if (window.__cartReadinessInit) return;
  window.__cartReadinessInit = true;

  // Canadian FSA first letter -> province code. X covers NT and NU (NT default).
  const FSA_PROVINCE = {
    A: 'NL', B: 'NS', C: 'PE', E: 'NB', G: 'QC', H: 'QC', J: 'QC',
    K: 'ON', L: 'ON', M: 'ON', N: 'ON', P: 'ON', R: 'MB', S: 'SK',
    T: 'AB', V: 'BC', X: 'NT', Y: 'YT'
  };
  const STORAGE_KEY = 'mru-cart-readiness-postal';

  // Bilingual strings (2026-07-22): JS-rendered text follows the document
  // language so re-enabling the estimator stays FR-safe (Quebec French).
  const IS_FR = (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
  const T = IS_FR ? {
    estimatedTotal: 'Total estimé',
    plusTax: 'plus taxes',
    subtotal: 'Sous-total',
    beforeShipTax: 'avant livraison et taxes',
    estimateShipping: 'Estimer la livraison',
    invalidPostal: 'Entrez un code postal valide (ex. B3B 1L9).',
    gettingLive: 'Chargement des tarifs…',
    noRates: 'Aucun tarif pour ce code postal. Les options exactes s’affichent au paiement.',
    cantEstimate: 'Estimation impossible pour le moment. Les tarifs exacts s’affichent au paiement.',
    shippingEstimate: 'Estimation de livraison',
    change: 'Modifier',
    estAbbrev: 'env.',
    businessDays: 'jours ouvrables',
    dateLocale: 'fr-CA',
    gettingFor: function (p) { return 'Tarifs pour ' + p + '…'; },
    moreOptions: function (n) { return '+' + n + ' autre' + (n > 1 ? 's' : '') + ' option' + (n > 1 ? 's' : '') + ' au paiement'; },
  } : {
    estimatedTotal: 'Estimated total',
    plusTax: 'plus tax',
    subtotal: 'Subtotal',
    beforeShipTax: 'before shipping and tax',
    estimateShipping: 'Estimate shipping',
    invalidPostal: 'Enter a valid postal code (e.g. B3B 1L9).',
    gettingLive: 'Getting live rates…',
    noRates: 'No rates for that postal code. You’ll see exact options at checkout.',
    cantEstimate: 'Couldn’t estimate right now. Exact rates are shown at checkout.',
    shippingEstimate: 'Shipping estimate',
    change: 'Change',
    estAbbrev: 'est.',
    businessDays: 'business days',
    dateLocale: 'en-CA',
    gettingFor: function (p) { return 'Getting rates for ' + p + '…'; },
    moreOptions: function (n) { return '+' + n + ' more option' + (n > 1 ? 's' : '') + ' at checkout'; },
  };

  const isValidPostal = (p) => /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(p.trim());

  const formatMoney = (price) => {
    const n = parseFloat(price);
    return Number.isNaN(n) ? price : `$${n.toFixed(2)}`;
  };

  const cartRoute = () => (window.theme && theme.routes && theme.routes.cart) || '/cart';

  const getSaved = () => { try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; } };
  const setSaved = (p) => { try { localStorage.setItem(STORAGE_KEY, p); } catch (e) { /* private mode */ } };

  const els = (root) => ({
    toggle: root.querySelector('[data-cr-toggle]'),
    toggleLabel: root.querySelector('[data-cr-toggle-label]'),
    body: root.querySelector('[data-cr-body]'),
    input: root.querySelector('[data-cr-postal]'),
    go: root.querySelector('[data-cr-go]'),
    result: root.querySelector('[data-cr-rates]')
  });

  // --- Ledger total anchor ---

  function anchorEls(ship) {
    const panel = ship.closest('[data-cart-readiness]');
    const ledger = panel ? panel.closest('[data-crl]') : null;
    if (!ledger) return null;
    return {
      label: ledger.querySelector('[data-cr-total-label]'),
      sub: ledger.querySelector('[data-cr-total-sub]'),
      value: ledger.querySelector('[data-cr-total-value]'),
      cents: parseFloat(panel.getAttribute('data-cr-total-cents') || '0')
    };
  }

  function setAnchorEstimated(ship, ratePrice) {
    const a = anchorEls(ship);
    if (!a || !a.value) return;
    const rate = parseFloat(ratePrice);
    if (Number.isNaN(rate) || !a.cents) return;
    if (a.label) a.label.textContent = T.estimatedTotal;
    if (a.sub) a.sub.textContent = T.plusTax;
    a.value.textContent = formatMoney(a.cents / 100 + rate);
  }

  function resetAnchor(ship) {
    const a = anchorEls(ship);
    if (!a || !a.value) return;
    if (a.label) a.label.textContent = T.subtotal;
    if (a.sub) a.sub.textContent = T.beforeShipTax;
    if (a.cents) a.value.textContent = formatMoney(a.cents / 100);
  }

  function setState(ship, state) {
    const e = els(ship);
    ship.setAttribute('data-state', state);
    if (state === 'collapsed') {
      if (e.toggle) { e.toggle.hidden = false; e.toggle.setAttribute('aria-expanded', 'false'); e.toggle.disabled = false; }
      if (e.toggleLabel) e.toggleLabel.textContent = T.estimateShipping;
      if (e.body) e.body.hidden = true;
      if (e.result) e.result.innerHTML = '';
      resetAnchor(ship);
    } else if (state === 'expanded') {
      if (e.toggle) { e.toggle.hidden = false; e.toggle.setAttribute('aria-expanded', 'true'); e.toggle.disabled = false; }
      if (e.toggleLabel) e.toggleLabel.textContent = T.estimateShipping;
      if (e.body) e.body.hidden = false;
      if (e.result) e.result.innerHTML = '';
      resetAnchor(ship);
    } else if (state === 'estimated') {
      if (e.toggle) e.toggle.hidden = true;
      if (e.body) e.body.hidden = true;
    }
  }

  async function fetchRates(postal) {
    const province = FSA_PROVINCE[postal[0]];
    const params = `shipping_address[zip]=${encodeURIComponent(postal)}`
      + `&shipping_address[province]=${encodeURIComponent(province)}`
      + '&shipping_address[country]=CA';
    let rates = [];
    for (let attempt = 0; attempt < 4; attempt += 1) {
      if (attempt > 0) await new Promise((r) => setTimeout(r, 1200));
      const response = await fetch(`${cartRoute()}/shipping_rates.json?${params}`);
      if (response.ok) {
        const data = await response.json();
        rates = (data.shipping_rates || []).slice().sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        if (rates.length) break;
      }
    }
    return rates;
  }

  // "Jul 3-5" same month, "Jun 30-Jul 2" across months, "3-5 business days"
  // when the API returns day counts. Hyphens only (voice rule: no dashes).
  const fmtWindow = (rate) => {
    const range = rate.delivery_range || rate.delivery_days;
    if (!Array.isArray(range) || !range.length) return '';
    const a = String(range[0]);
    const b = String(range[range.length - 1]);
    if (/^\d{4}-\d{2}-\d{2}/.test(a)) {
      const toDate = (s) => new Date(`${s.slice(0, 10)}T12:00:00`);
      const md = (dt) => dt.toLocaleDateString(T.dateLocale, { month: 'short', day: 'numeric' });
      if (a === b) return md(toDate(a));
      const da = toDate(a);
      const db = toDate(b);
      if (da.getMonth() === db.getMonth() && da.getFullYear() === db.getFullYear()) {
        return `${md(da)}-${db.getDate()}`;
      }
      return `${md(da)}-${md(db)}`;
    }
    return a === b ? `${a} ${T.businessDays}` : `${a}-${b} ${T.businessDays}`;
  };

  async function estimate(ship, postalRaw) {
    const e = els(ship);
    const postal = (postalRaw || e.input?.value || '').trim().toUpperCase();
    if (!isValidPostal(postal)) {
      if (e.result) e.result.innerHTML = '<p class="cart-readiness__err">' + T.invalidPostal + '</p>';
      return;
    }
    if (e.go) e.go.disabled = true;
    if (e.result) e.result.innerHTML = '<p class="cart-readiness__sub">' + T.gettingLive + '</p>';
    try {
      const rates = await fetchRates(postal);
      if (!rates.length) {
        if (e.result) e.result.innerHTML = '<p class="cart-readiness__err">' + T.noRates + '</p>';
        return;
      }
      setSaved(postal);
      const best = rates[0];
      const more = rates.length - 1;
      const win = fmtWindow(best);
      const moreTxt = more > 0 ? ` &middot; ${T.moreOptions(more)}` : '';
      if (e.result) {
        e.result.innerHTML = `
          <p class="cart-readiness__best">
            <span class="cart-readiness__best-lbl">${T.shippingEstimate} &middot; <strong>${postal}</strong></span>
            <span class="cart-readiness__best-price">${formatMoney(best.price)}</span>
          </p>
          <p class="cart-readiness__sub">${best.name}${win ? ` &middot; ${T.estAbbrev} ${win}` : ''} &middot; <button type="button" class="cart-readiness__change" data-cr-change>${T.change}</button>${moreTxt}</p>`;
      }
      setState(ship, 'estimated');
      setAnchorEstimated(ship, best.price);
    } catch (err) {
      if (e.result) e.result.innerHTML = '<p class="cart-readiness__err">' + T.cantEstimate + '</p>';
    } finally {
      if (e.go) e.go.disabled = false;
    }
  }

  // --- Delegated events (survive re-renders) ---

  document.addEventListener('click', (evt) => {
    const toggle = evt.target.closest('[data-cr-toggle]');
    if (toggle) {
      evt.preventDefault();
      const ship = toggle.closest('[data-cr-ship]');
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      setState(ship, expanded ? 'collapsed' : 'expanded');
      if (!expanded) ship.querySelector('[data-cr-postal]')?.focus();
      return;
    }
    const go = evt.target.closest('[data-cr-go]');
    if (go) {
      evt.preventDefault();
      estimate(go.closest('[data-cr-ship]'));
      return;
    }
    const change = evt.target.closest('[data-cr-change]');
    if (change) {
      evt.preventDefault();
      const ship = change.closest('[data-cr-ship]');
      setState(ship, 'expanded');
      const input = ship.querySelector('[data-cr-postal]');
      if (input) { input.value = getSaved() || ''; input.focus(); }
    }
  });

  // The postal input lives inside the cart <form>; Enter must estimate, not submit.
  document.addEventListener('keydown', (evt) => {
    if (evt.key !== 'Enter') return;
    if (!evt.target.matches || !evt.target.matches('[data-cr-postal]')) return;
    evt.preventDefault();
    estimate(evt.target.closest('[data-cr-ship]'));
  });

  // --- Boot: auto-upgrade to estimated state when a postal is saved ---

  function boot() {
    document.querySelectorAll('[data-cart-readiness][data-cr-full]:not([data-cr-init])').forEach((panel) => {
      panel.setAttribute('data-cr-init', '');
      const ship = panel.querySelector('[data-cr-ship]');
      if (!ship) return;
      const saved = getSaved();
      if (!saved || !isValidPostal(saved)) return;
      const e = els(ship);
      if (e.input) e.input.value = saved;
      if (e.toggle) { e.toggle.disabled = true; }
      if (e.toggleLabel) e.toggleLabel.textContent = T.gettingFor(saved);
      estimate(ship, saved).then(() => {
        // estimate() left state collapsed-ish on failure: restore the link
        if (ship.getAttribute('data-state') !== 'estimated') setState(ship, 'collapsed');
      });
    });
  }

  boot();
  new MutationObserver(() => boot()).observe(document.body, { childList: true, subtree: true });
})();


/**
 * Overselling block beacon (2026-09-02).
 *
 * The "Cut-to-length stock check (MRU)" checkout rule is a Shopify Function
 * that refuses a cart drawing more inches than the shared pile holds. It runs
 * sandboxed, so it cannot tell anyone. Shopify answers the cart call with a
 * 422 carrying the function's own message, and every path that adds or
 * changes a line (the PDP configurator, the collection rail, the cart page and
 * drawer) goes through fetch, so one wrapper sees them all. A matching 422 is
 * POSTed to MRU Online, which pings the team (bell + push) so someone can check
 * the rack fast. Payload = the message plus the current cart lines from
 * /cart.js: no customer details. Failing silently is the rule: the beacon
 * never changes the cart call's result, and a beacon error is swallowed.
 */
(() => {
  if (window.__stockPoolBeaconInit) return;
  window.__stockPoolBeaconInit = true;

  const ENDPOINT = 'https://orders.metalsrus.ca/api/stock-pool-block';
  // Shared with the dashboard's STOCK_POOL_ALERT_TOKEN. It ships in a public
  // asset on purpose: a spoofed beacon is bell noise, nothing more.
  const TOKEN = '59419db81adaa7a0e38034f08ffdbf990aa174a97c8aed6f';
  const CART_CALL = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
  // The function's wording, and nothing else: a plain sold-out 422 from
  // Shopify's own per-variant check reads differently and is not our case.
  const STOCK_BLOCK = /have enough .* in stock to fulfill/i;
  let lastSentAt = 0;

  const send = (message) => {
    const now = Date.now();
    if (now - lastSentAt < 15000) return;
    lastSentAt = now;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .catch(() => ({ items: [] }))
      .then((cart) => fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-MRU-Alert-Token': TOKEN },
        body: JSON.stringify({
          message,
          cart: { items: (cart && cart.items ? cart.items : []).map((i) => ({
            product_id: i.product_id, title: i.product_title || i.title, quantity: i.quantity, properties: i.properties || {}
          })) }
        }),
        keepalive: true
      }))
      .catch(() => {});
  };

  const onMessage = (msg) => { if (msg && STOCK_BLOCK.test(String(msg))) send(String(msg)); };

  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const result = origFetch.apply(this, arguments);
    if (!CART_CALL.test(url)) return result;
    return result.then((res) => {
      if (res && res.status === 422) {
        res.clone().json().then((j) => onMessage(j && (j.message || j.description))).catch(() => {});
      }
      return res;
    });
  };

  // Belt for components that surface the error as an event instead.
  document.addEventListener('on:cart:error', (e) => onMessage(e && e.detail && e.detail.error));
})();

/**
 * Cart boosters (2026-11-02, CRO review Move 3): one-tap buttons rendered by
 * snippets/cart-boosters.liquid inside the drawer's order summary.
 *   [data-cb-more] line=N qty=Q  -> /cart/change.js (one more piece of an
 *                                    existing preset line; properties kept)
 *   [data-cb-add] variant-id, cut -> /cart/add.js with `Your Cut`, exactly
 *                                    like the PDP configurator's preset add
 * Both ask for the drawer + icon sections and hand the response to the
 * drawer's own renderContents, the same path the configurator uses.
 * Delegated on document because the markup arrives by Ajax.
 */
(() => {
  if (window.__cartBoostersInit) return;
  window.__cartBoostersInit = true;

  const isFr = () => (document.documentElement.lang || '').toLowerCase().startsWith('fr');

  const sectionsParam = () => {
    const drawer = document.querySelector('cart-drawer');
    let ids = 'cart-icon-bubble';
    const sec = drawer && drawer.closest('.shopify-section');
    if (sec) ids += ',' + sec.id.replace('shopify-section-', '');
    return ids;
  };

  const showError = (btn, msg) => {
    const box = btn.closest('[data-cb-addons]');
    const err = box && box.querySelector('[data-cb-err]');
    if (err) {
      err.textContent = msg || (isFr() ? 'Impossible d’ajouter cette pièce. Réessayez.' : 'Couldn’t add that piece. Try again.');
      err.hidden = false;
    }
  };

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cb-add], [data-cb-more]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    const more = btn.hasAttribute('data-cb-more');
    const body = more
      ? { line: Number(btn.dataset.line), quantity: Number(btn.dataset.qty) }
      : { items: [{ id: Number(btn.dataset.variantId), quantity: 1, properties: { 'Your Cut': btn.dataset.cut } }] };
    body.sections = sectionsParam();
    body.sections_url = window.location.pathname;

    try {
      const res = await fetch(more ? '/cart/change.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data && (data.description || data.message));
      const drawer = document.querySelector('cart-drawer');
      if (drawer && data.sections && typeof drawer.renderContents === 'function') {
        drawer.renderContents(data, false);
      } else {
        window.location.reload();
      }
    } catch (err) {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      showError(btn, err && err.message);
    }
  });
})();
